import { useMemo, useState, useEffect, useContext } from 'react';

import { calculateStatsFromTrainingData, transformCubeResponse } from './helpers';
import { getSpendByMMMFriendlyName } from '../../api/cube';
import { FeatureAliasesContext } from '../../context/MMMFeatureAliases';
import { findFeaturedActionId } from '../../utils/options';
import { dateIsBetween, getNextDay } from '../../views/MarketingMixModeling/parts/time';
import { useGlobalState } from '../global';

const useFeatureDates = ({ startDate, endDate, trainingDateRange }) => {
  const isDateInTrainingRange = date => dateIsBetween(date, trainingDateRange[0], trainingDateRange[1]);
  const [cubeDates, setCubeDates] = useState(null);

  useEffect(() => {
    if (!startDate || !endDate || !trainingDateRange) {
      setCubeDates(null);
      return;
    }

    const startInRange = isDateInTrainingRange(startDate);
    const endInRange = isDateInTrainingRange(endDate);

    if (!startInRange || !endInRange) {
      const effectiveStartDate = startInRange ? getNextDay(trainingDateRange[1]) : startDate;
      setCubeDates(current => {
        // Compare values before creating new array
        if (current?.[0] === effectiveStartDate && current?.[1] === endDate) {
          return current;
        }
        return [effectiveStartDate, endDate];
      });
    } else {
      setCubeDates(null);
    }
  }, [startDate, endDate, trainingDateRange]);

  return { cubeDates, isDateInTrainingRange };
};

const useMMMSpendCubeData = ({ cubeDates, responseCurveFeatures, featureAliasesMap, segId = 0 }) => {
  const { jwt, segments } = useGlobalState();
  const featuredSegment = segId || findFeaturedActionId(segments);
  const [loading, setLoading] = useState(false);
  const [cubeStats, setCubeStats] = useState(null);
  const featureIdMap = useMemo(() => responseCurveFeatures.reduce((accu, key) => {
    accu[featureAliasesMap[key]] = key;
    return accu;
  }, {}), [featureAliasesMap]);

  useEffect(() => {
    if (!cubeDates || !Object.keys(featureIdMap).length) {
      setCubeStats(null);
      setLoading(false);
      return;
    }
    setLoading(true);

    let isSubscribed = true;

    getSpendByMMMFriendlyName(jwt, featuredSegment, cubeDates[0], cubeDates[1])
      .then(resp => {
        if (isSubscribed) {
          setCubeStats(transformCubeResponse(resp, featureIdMap, cubeDates));
          setLoading(false);
        }
      })
      .catch(error => {
        console.error('Error fetching spend data:', error);
        if (isSubscribed) {
          setCubeStats(null);
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [jwt, featuredSegment, cubeDates, featureIdMap]);

  return { loading, cubeStats };
};

export const useFeatureSpend = ({ trainingData, trainingDateRange, responseCurveFeatures, startDate, endDate, segId }) => {
  const { featureAliasesMap } = useContext(FeatureAliasesContext);
  const { cubeDates, isDateInTrainingRange } = useFeatureDates({ startDate, endDate, trainingDateRange });
  const { loading, cubeStats } = useMMMSpendCubeData({ cubeDates, responseCurveFeatures, segId, featureAliasesMap });

  const selectedTrainingData = useMemo(() => {
    if (!trainingData || loading) return [];

    const startInRange = isDateInTrainingRange(startDate);
    const endInRange = isDateInTrainingRange(endDate);

    if (!startInRange) return [];

    const effectiveEndDate = endInRange ? endDate : trainingDateRange[1];
    return trainingData.filter(row => dateIsBetween(row.date, startDate, effectiveEndDate));
  }, [startDate, endDate, trainingData]);

  const featureStats = useMemo(() => {
    const trainingStats = calculateStatsFromTrainingData(selectedTrainingData, trainingData);

    const baseStats = Object.entries(trainingStats).reduce((stats, [key, value]) => ({
      ...stats,
      [key]: {
        ...value,
        friendlyName: featureAliasesMap[key] || 'Unclassified Spend',
      },
    }), {});

    if (!cubeStats) return baseStats;

    return Object.entries(cubeStats).reduce((stats, [key, value]) => ({
      ...stats,
      [key]: {
        ...baseStats[key],
        currentSpend: (baseStats[key]?.currentSpend || 0) + (value?.currentSpend || 0),
      },
    }), baseStats);
  }, [selectedTrainingData, cubeStats]);

  return {
    featureStats,
    loading,
  };
};
